import { getPersonaData, personaIdToProfession } from '../personas';
import { detectPopulatedSections } from './common/sectionUtils';

/**
 * Service for loading and applying persona data using existing import infrastructure
 */

/**
 * Loads persona data by profession name and applies it to the application context
 * @param {string} professionName - The name of the profession
 * @param {Object} contextSetters - Context setter functions from App.js
 * @returns {Promise<boolean>} - Whether the persona was successfully loaded and applied
 */
export const loadAndApplyPersona = async (professionName, contextSetters) => {
  try {
    console.log(`PersonaService: Loading persona for profession: ${professionName}`);
    
    // Load persona data
    const personaData = await getPersonaData(professionName);
    
    if (!personaData || !personaData.formData) {
      console.warn(`PersonaService: No persona data found for profession: ${professionName}`);
      return false;
    }

    // Prepare the persona data in the same format expected by ImportService
    const parsedResult = {
      formData: personaData.formData,
    };

    // Use the silent application method for persona loading
    const success = applyPersonaDataSilently(parsedResult, contextSetters);

    if (success) {
      console.log(`PersonaService: Successfully applied persona for ${professionName}`);
      return true;
    } else {
      console.warn(`PersonaService: Failed to apply persona for ${professionName}`);
      return false;
    }

  } catch (error) {
    console.error(`PersonaService: Error loading persona for ${professionName}:`, error);
    return false;
  }
};

/**
 * Loads persona data by persona ID (URL-safe version)
 * @param {string} personaId - The persona ID from URL parameters
 * @param {Object} contextSetters - Context setter functions from App.js
 * @returns {Promise<boolean>} - Whether the persona was successfully loaded and applied
 */
export const loadAndApplyPersonaById = async (personaId, contextSetters) => {
  try {
    // Convert persona ID back to profession name
    const professionName = personaIdToProfession(personaId);
    
    if (!professionName) {
      console.warn(`PersonaService: No profession found for persona ID: ${personaId}`);
      return false;
    }

    return await loadAndApplyPersona(professionName, contextSetters);
  } catch (error) {
    console.error(`PersonaService: Error loading persona by ID ${personaId}:`, error);
    return false;
  }
};

/**
 * Create a silent version of updateAppContext for persona loading (no confirmation dialog)
 * @param {Object} parsedResult - The parsed persona data
 * @param {Object} contextSetters - Context setter functions
 * @returns {boolean} - Whether the application was successful
 */
export const applyPersonaDataSilently = (parsedResult, contextSetters) => {
  const { updateFormData, setSelectedSections, allSections } = contextSetters;

  if (!updateFormData || !setSelectedSections) {
    console.error('PersonaService: Missing required context setters (updateFormData, setSelectedSections).');
    return false;
  }

  try {
    const { formData: importedFormData } = parsedResult;
    updateFormData(null, importedFormData);

    // Auto-detect and set sections based on the new data
    if (importedFormData && allSections) {
      const finalSections = detectPopulatedSections(importedFormData, allSections);
      console.log('PersonaService: Auto-selecting populated sections:', finalSections.map(s => s.name));
      setSelectedSections(finalSections);
    } else if (!allSections) {
      console.warn('PersonaService: allSections not available, skipping section auto-selection');
    }

    console.log('PersonaService: Application context updated with persona data.');
    return true;

  } catch (error) {
    console.error('PersonaService: Error applying persona data:', error);
    return false;
  }
}; 